import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  ChatCircleDots,
  Compass,
  FileText,
  Target,
} from "@phosphor-icons/react";
import WhistleIcon from "@/components/WhistleIcon";
import { trackPublicEvent } from "@/lib/publicAnalytics";
import SeoPageShell from "@/components/SeoPageShell";

const carryForwardLayers = [
  {
    icon: Compass,
    label: "Relationship state",
    title: "Start from what is true now, not from a generic agenda.",
    copy: "Carry forward the active objection, stakeholder dynamics, commitments, buying intent, risk, and anything that materially changed in the previous conversation.",
  },
  {
    icon: ChatCircleDots,
    label: "Open loops",
    title: "Separate what was discussed from what is still unresolved.",
    copy: "A topic being mentioned does not mean it was settled. Identify the questions, concerns, promises, and decision dependencies that still need movement.",
  },
  {
    icon: WhistleIcon,
    label: "Coaching",
    title: "Bring the lesson forward, not just the customer facts.",
    copy: "If a moment could have been handled differently, turn that into a specific behavioral reminder for the next call instead of relearning the same lesson afterward.",
  },
  {
    icon: Target,
    label: "Next-call objective",
    title: "Know what the next conversation needs to accomplish.",
    copy: "Translate the relationship state into what to validate, what to ask, what to avoid, and which outcome would actually move the customer relationship forward.",
  },
];

const prepChecklist = [
  "What changed in the customer relationship during the previous call?",
  "Which objection, risk, or decision dependency is still active?",
  "Which stakeholder matters next, and why?",
  "What did you promise to follow up on or validate?",
  "What should you handle differently based on the previous conversation?",
  "What concrete outcome would make the next call successful?",
];

function trackCta(section, ctaId) {
  trackPublicEvent("cta_click", {
    source: "prepare_for_next_customer_call",
    section,
    cta_id: ctaId,
  });
}

export default function PrepareForNextCustomerCallPage() {
  return (
    <SeoPageShell source="prepare_for_next_customer_call">
      <main>
        <section className="seo-marketing-hero product-feature-hero relative overflow-hidden px-5 pb-20 pt-16 sm:px-8 sm:pb-24 sm:pt-24">
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className="absolute left-[-12%] top-[-14rem] h-[36rem] w-[36rem] rounded-full bg-indigo-700/20 blur-[135px]" />
            <div className="absolute right-[-8%] top-[2rem] h-[28rem] w-[28rem] rounded-full bg-violet-700/10 blur-[135px]" />
          </div>
      
          <div className="relative mx-auto max-w-7xl">
            <div className="seo-marketing-hero__pill landing-anti-summary-pill mb-6 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs uppercase tracking-[0.2em] marketing-text-secondary">
              <span className="landing-anti-summary-icon" aria-hidden="true">
                <ArrowRight size={14} weight="bold" />
              </span>
              <span>Prepare for the next customer call</span>
            </div>
      
            <h1 className="seo-marketing-hero__title product-feature-hero__title landing-hero-title max-w-5xl">
              How to Prepare for the Next Customer Call
              <span className="sales-call-review-gradient product-feature-hero__title-accent landing-hero-spectrum block">Using the Previous Conversation</span>
            </h1>
      
            <p className="seo-marketing-hero__subcopy product-feature-hero-subcopy landing-hero-subcopy marketing-text-secondary">
              Good call preparation should not begin with a blank page, a generic checklist, or a summary you barely remember. It should begin with the actual relationship state left by the previous conversation—and turn that context into what to validate, what to ask, what to avoid, and what the next call needs to accomplish.
            </p>
      
            <div className="seo-marketing-hero__actions product-feature-hero__actions">
              <Link
                to="/login?mode=register"
                onClick={() => trackCta("hero", "sign_up_free")}
                className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition"
              >
                Sign up free
              </Link>
              <Link
                to="/demo"
                onClick={() => trackCta("hero", "see_demo")}
                className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition"
              >
                See Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      
        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <span className="sales-call-review-kicker">The preparation problem</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                A previous call can leave you with notes—or with a usable relationship state.
              </h2>
              <p className="mt-6 text-lg leading-8 text-slate-400">
                A transcript or summary is useful for retrieval. But when the next call is approaching, the more important question is whether you can quickly reconstruct what is still alive in the relationship and convert it into a plan.
              </p>
            </div>
      
            <div className="prepare-next-call-compare mt-12">
              <article className="prepare-next-call-compare__column">
                <div className="prepare-next-call-compare__eyebrow">
                  <FileText size={19} weight="duotone" />
                  PREVIOUS CALL AS NOTES
                </div>
                <h3>You remember what was discussed.</h3>
                <div className="prepare-next-call-compare__items">
                  <span>Summary and action items</span>
                  <span>Loose quote fragments</span>
                  <span>A list of topics</span>
                  <span>Your own memory of the call</span>
                </div>
                <p>
                  Enough to refresh yourself, but easy to lose the difference between what was mentioned, what was resolved, and what actually changed.
                </p>
              </article>
      
              <div className="prepare-next-call-compare__divider" aria-hidden="true">
                <ArrowRight size={24} weight="bold" />
              </div>
      
              <article className="prepare-next-call-compare__column is-context">
                <div className="prepare-next-call-compare__eyebrow">
                  <Compass size={19} weight="duotone" />
                  PREVIOUS CALL AS CONTEXT
                </div>
                <h3>You know what the relationship needs next.</h3>
                <div className="prepare-next-call-compare__items">
                  <span>Active objection and unresolved risk</span>
                  <span>Stakeholder and decision dynamics</span>
                  <span>Commitments and open loops</span>
                  <span>Buying intent or churn-risk movement</span>
                </div>
                <p>
                  This is what makes preparation actionable: the previous conversation becomes the starting state for the next one rather than a document to reread.
                </p>
              </article>
            </div>
          </div>
        </section>
      
        <section id="from-history-to-plan" className="scroll-mt-24 px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <span className="sales-call-review-kicker">From history to plan</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Four things should carry from the previous conversation into the next one.
              </h2>
              <p className="mt-6 text-lg leading-8 text-slate-400">
                The goal is not to create a longer pre-call brief. It is to preserve the few pieces of context that materially change how you should enter the conversation.
              </p>
            </div>
      
            <div className="prepare-next-call-layers mt-12">
              {carryForwardLayers.map(({ icon: Icon, label, title, copy }, index) => (
                <article key={label} className="prepare-next-call-layer">
                  <div className="prepare-next-call-layer__topline">
                    <span>0{index + 1}</span>
                    <Icon size={21} weight="duotone" />
                  </div>
                  <span className="prepare-next-call-layer__label">{label}</span>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      
        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-start lg:gap-16">
            <div className="lg:sticky lg:top-24">
              <span className="sales-call-review-kicker">A concrete example</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Preparation gets easier when the previous call has already been interpreted.
              </h2>
              <p className="mt-6 text-lg leading-8 text-slate-400">
                The same customer statement can become relationship memory, a coaching lesson, and a focused plan for the next conversation.
              </p>
            </div>
      
            <div className="prepare-next-call-example">
              <div className="prepare-next-call-example__quote">
                <span>CLIENT · PREVIOUS CALL</span>
                <blockquote>
                  “The budget is approved. I just need our operations lead to be comfortable with the implementation before I can bring this to finance.”
                </blockquote>
              </div>
      
              <div className="prepare-next-call-example__row">
                <div className="prepare-next-call-example__label">
                  <Compass size={18} weight="duotone" />
                  RELATIONSHIP
                </div>
                <p>
                  Budget is no longer the blocker. The decision now depends on operational confidence, with the operations lead becoming the critical stakeholder before finance.
                </p>
              </div>
      
              <div className="prepare-next-call-example__row">
                <div className="prepare-next-call-example__label">
                  <WhistleIcon size={18} weight="duotone" />
                  COACHING
                </div>
                <p>
                  Stop strengthening the ROI case. Use the next conversation to make implementation risk concrete and discuss what the operations lead needs to see to support the change.
                </p>
              </div>
      
              <div className="prepare-next-call-example__next">
                <div className="prepare-next-call-example__label">
                  <Target size={18} weight="duotone" />
                  PREPARE NEXT CALL
                </div>
                <div className="prepare-next-call-example__plan">
                  <div><strong>Validate</strong><span>Whether implementation confidence is the only remaining blocker before finance.</span></div>
                  <div><strong>Ask</strong><span>What would the operations lead need to see or hear to support the rollout?</span></div>
                  <div><strong>Avoid</strong><span>Reopening price or ROI arguments that the customer has already accepted.</span></div>
                  <div><strong>Outcome</strong><span>Agree on the operations validation step and a clear path to finance.</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>
      
        <section className="px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
            <div>
              <span className="sales-call-review-kicker">A useful next-call brief</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
                If the prep cannot answer these questions, it is probably still just notes.
              </h2>
              <p className="mt-6 text-lg leading-8 text-slate-400">
                The best preparation is compact because the relationship context has already done the filtering for you.
              </p>
            </div>
      
            <div className="grid gap-4">
              {prepChecklist.map((item) => (
                <div key={item} className="sales-call-review-check">
                  <CheckCircle size={21} weight="fill" />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      
        <section className="px-5 pb-10 sm:px-8 sm:pb-14">
          <div className="conversation-intelligence-related mx-auto max-w-5xl">
            <div>
              <span className="sales-call-review-kicker">Why this is more than summarization</span>
              <h2>Preparation depends on interpretation, not just recall.</h2>
              <p>
                If you want the category distinction behind this workflow, see why conversation intelligence should connect evidence to meaning and next action rather than stop at a summary.
              </p>
            </div>
            <Link
              to="/conversation-intelligence-vs-call-summarization"
              onClick={() => trackCta("internal_link", "conversation_intelligence_vs_call_summarization")}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-5 py-3.5 font-medium text-slate-200 transition hover:border-white/20 hover:bg-white/[0.08]"
            >
              Conversation intelligence vs summarization
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </section>
      
        <section className="px-5 pb-20 pt-6 sm:px-8 sm:pb-28 sm:pt-10">
          <div className="product-feature-final sales-call-review-cta mx-auto max-w-5xl">
            <span className="sales-call-review-kicker">See the relationship carry forward</span>
            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
              Enter the next call with the customer history already attached to the plan.
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-400">
              Explore how Callsect carries Dissection, evidence-backed Coaching, Relationship context, and Prepare Next Call across a synthetic sales conversation.
            </p>
            <div>
              <Link
                to="/login?mode=register"
                onClick={() => trackCta("final", "sign_up_free")}
                className="product-feature-final__primary"
              >
                Sign up free
              </Link>
              <Link
                to="/demo"
                onClick={() => trackCta("final", "see_demo")}
                className="product-feature-final__secondary"
              >
                See Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </SeoPageShell>
  );
}
